// "How to fix it", read from a finding (#1277).
//
// The scanner writes its advice and its risk explanation in Markdown, and its example
// commands with `<PLACEHOLDER>`s. This reads both into what the console can show safely:
//
// - Text becomes a bounded set of tokens, rendered as React nodes and never as HTML:
//   paragraphs, lists (`-`, `*`, `+` or `1.`), **bold**, *emphasis*, `inline code` and
//   [links](https://…). Nothing else is Markdown here: raw HTML, headings and the rest stay
//   the characters they are, and a link to anything but `http:` or `https:` is its label alone.
// - A placeholder is filled only by an entry in `PLACEHOLDERS`, from a value the finding
//   carries (its region, its account, its resource's name or ARN), only where the resource is
//   the kind the command asks for, and only when the value is a valid value of its source.
//   A filled value is shell-quoted where it needs to be. Anything else stays marked.
// - Every command carries the same review label: some are destructive as written.

import type { Finding } from "../api/posture";

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "strong"; children: Inline[] }
  | { kind: "em"; children: Inline[] }
  | { kind: "link"; href: string; children: Inline[] };

export type Block =
  | { kind: "paragraph"; children: Inline[] }
  | { kind: "list"; ordered: boolean; items: Inline[][] };

export type CommandPart =
  | { kind: "text"; text: string }
  | { kind: "value"; text: string }
  | { kind: "placeholder"; name: string };

export interface Command {
  label: string;
  /** The command as it would be copied, an unfilled placeholder as `<NAME>`. */
  text: string;
  parts: CommandPart[];
  /** The placeholders left for the reader to fill, each once, in the order they appear. */
  unresolved: string[];
}

export interface FixAdvice {
  recommendation: Block[];
  risk: Block[];
  /** The check's reference page, when it is an http(s) address. */
  reference: string | null;
  command: Command | null;
}

export const COMMAND_LABEL = "Example: review before running";

/** What a finding offers under "How to fix it", or null when it offers nothing. */
export function fixAdvice(finding: Finding): FixAdvice | null {
  const remediation = finding.remediation ?? {};
  const recommendation = readMarkdown(remediation.recommendation ?? "");
  const reference = webAddress((remediation.url ?? "").trim());
  const cli = (remediation.cli ?? "").trim();
  const command = cli === "" ? null : fillCommand(cli, finding);
  if (recommendation.length === 0 && reference === null && command === null) return null;
  return { recommendation, risk: readMarkdown(finding.risk ?? ""), reference, command };
}

// ── text ─────────────────────────────────────────────────────────────────────

const LIST_ITEM = /^\s*([-*+]|\d{1,9}[.)])\s+(.*)$/;

export function readMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] | null = null;
  let list: { ordered: boolean; items: string[] } | null = null;
  const close = () => {
    if (paragraph !== null) blocks.push({ kind: "paragraph", children: readInline(paragraph.join(" ")) });
    if (list !== null) blocks.push({ kind: "list", ordered: list.ordered, items: list.items.map(readInline) });
    paragraph = null;
    list = null;
  };
  for (const line of source.replace(/\r\n?/g, "\n").split("\n")) {
    if (line.trim() === "") {
      close();
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item !== null) {
      const ordered = /\d/.test(item[1]);
      if (list === null || list.ordered !== ordered) {
        close();
        list = { ordered, items: [] };
      }
      list.items.push(item[2].trim());
    } else if (list !== null && /^\s/.test(line)) {
      // An indented line carries on the item above it.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else if (paragraph !== null) {
      paragraph.push(line.trim());
    } else {
      close();
      paragraph = [line.trim()];
    }
  }
  close();
  return blocks;
}

const LINK = /^\[([^[\]]*)\]\(((?:[^\s()]|\([^\s()]*\))*)\)/;

function readInline(source: string): Inline[] {
  const out: Inline[] = [];
  const text = (t: string) => {
    const last = out[out.length - 1];
    if (last !== undefined && last.kind === "text") last.text += t;
    else if (t !== "") out.push({ kind: "text", text: t });
  };
  let i = 0;
  while (i < source.length) {
    const rest = source.slice(i);
    if (rest[0] === "`") {
      const end = source.indexOf("`", i + 1);
      if (end > i + 1) {
        out.push({ kind: "code", text: source.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (rest.startsWith("**")) {
      const end = source.indexOf("**", i + 2);
      if (end > i + 2) {
        out.push({ kind: "strong", children: readInline(source.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (rest[0] === "*" && /^\*[A-Za-z0-9]/.test(rest) && (i === 0 || /[\s(]/.test(source[i - 1]))) {
      const close = /^\*([^*]*[A-Za-z0-9.)])\*(?![A-Za-z0-9*])/.exec(rest);
      if (close !== null) {
        out.push({ kind: "em", children: readInline(close[1]) });
        i += close[0].length;
        continue;
      }
    }
    if (rest[0] === "[") {
      const link = LINK.exec(rest);
      if (link !== null) {
        const children = readInline(link[1]);
        const href = webAddress(link[2]);
        if (href === null) children.forEach((c) => (c.kind === "text" ? text(c.text) : out.push(c)));
        else out.push({ kind: "link", href, children });
        i += link[0].length;
        continue;
      }
    }
    text(source[i]);
    i += 1;
  }
  return out;
}

/** The address, when it is an absolute http(s) one; anything else is no link. */
function webAddress(address: string): string | null {
  try {
    const url = new URL(address);
    return url.protocol === "http:" || url.protocol === "https:" ? address : null;
  } catch {
    return null;
  }
}

// ── commands ─────────────────────────────────────────────────────────────────

type Source = "region" | "account" | "name" | "arn" | "user_pool";

/**
 * How a placeholder is filled: from which of the finding's values, and, for a resource's
 * name, which kind of resource it must be (the ARN's service and resource type) and, where
 * one placeholder serves many commands, which option it must follow.
 */
interface Fill {
  source: Source;
  kind?: string;
  after?: string;
}

const named = (kind: string, ...after: string[]): Fill[] =>
  after.length === 0 ? [{ source: "name", kind }] : after.map((flag) => ({ source: "name", kind, after: flag }));

const PLACEHOLDERS: Readonly<Record<string, Fill[]>> = {
  REGION: [{ source: "region" }],
  ACCOUNT_ID: [{ source: "account" }],
  RESOURCE_ARN: [{ source: "arn" }],
  FUNCTION_NAME: named("lambda:function"),
  "function-name": named("lambda:function"),
  example_function_name: named("lambda:function"),
  BUCKET_NAME: named("s3:bucket"),
  bucket_name: named("s3:bucket"),
  ROLE_NAME: named("iam:role"),
  STACK_NAME: named("cloudformation:stack"),
  trail_name: named("cloudtrail:trail"),
  "alarm-name": named("cloudwatch:alarm"),
  DISTRIBUTION_ID: named("cloudfront:distribution"),
  USER_POOL_CLIENT_ID: named("cognito-idp:userpool-client"),
  USER_POOL_ID: [
    { source: "user_pool", kind: "cognito-idp:userpool" },
    { source: "user_pool", kind: "cognito-idp:userpool-client" },
  ],
  example_resource_name: [
    ...named("iam:role", "--role-name"),
    ...named("lambda:function", "--function-name"),
    ...named("cloudtrail:trail", "--trail-name"),
    ...named("logs:log-group", "--log-group-identifier", "--log-group-name"),
    ...named("s3:bucket", "--bucket"),
    ...named("cloudformation:stack", "--stack-name"),
  ],
  example_resource_id: [
    ...named("rds:snapshot", "--source-db-snapshot-identifier"),
    ...named("rds:db", "--db-instance-identifier"),
  ],
};

const PLACEHOLDER = /<([A-Za-z][A-Za-z0-9_-]*)>/g;
/** Characters a shell reads literally anywhere, so a value of only these needs no quotes. */
const SHELL_SAFE = /^[A-Za-z0-9._:/@%+=,-]+$/;

/** A user pool's id, from the ARN of the pool or of one of its clients. */
const USER_POOL = /^userpool\/([\w-]+_[0-9A-Za-z]+)(\/client\/[0-9A-Za-z]+)?$/;

/**
 * The resource's kind from its ARN: `service:type`, an S3 bucket being `s3:bucket` and a
 * user pool's app client `cognito-idp:userpool-client`.
 */
function resourceKind(arn: string): string | null {
  const parts = arn.split(":");
  if (parts.length < 6 || parts[0] !== "arn") return null;
  const resource = parts.slice(5).join(":");
  if (parts[2] === "s3") return resource.includes("/") ? null : "s3:bucket";
  if (parts[2] === "cognito-idp") {
    const pool = USER_POOL.exec(resource);
    return pool === null ? null : pool[2] === undefined ? "cognito-idp:userpool" : "cognito-idp:userpool-client";
  }
  const type = /^([A-Za-z0-9-]+)[/:]/.exec(resource);
  return type === null ? null : `${parts[2]}:${type[1]}`;
}

/** The finding's value for a source, when it is a valid one. */
function valueOf(source: Source, finding: Finding): string | null {
  if (source === "user_pool") {
    const parts = finding.resource_uid.split(":");
    return USER_POOL.exec(parts.slice(5).join(":"))?.[1] ?? null;
  }
  const value =
    source === "region"
      ? finding.region
      : source === "account"
        ? finding.account_id
        : source === "arn"
          ? finding.resource_uid
          : finding.resource_name;
  if (typeof value !== "string") return null;
  if (source === "region") return /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/.test(value) ? value : null;
  if (source === "account") return /^\d{12}$/.test(value) ? value : null;
  if (value === "" || value.length > 1024 || /[\u0000-\u001f\u007f]/.test(value)) return null;
  // A value the command would read as one of its own options is never put in.
  if (value.startsWith("-")) return null;
  if (source === "arn" && resourceKind(value) === null) return null;
  return value;
}

/** Where each placeholder sits: outside quotes, or inside single or double ones. */
function quoting(cli: string): ("none" | "single" | "double")[] {
  const at: ("none" | "single" | "double")[] = [];
  let state: "none" | "single" | "double" = "none";
  for (let i = 0; i < cli.length; i += 1) {
    at.push(state);
    const c = cli[i];
    if (state === "single") {
      if (c === "'") state = "none";
    } else if (c === "\\") {
      at.push(state);
      i += 1;
    } else if (state === "double") {
      if (c === '"') state = "none";
    } else if (c === "'") state = "single";
    else if (c === '"') state = "double";
  }
  return at;
}

function fill(name: string, before: string, finding: Finding): string | null {
  const flag = /(--[A-Za-z0-9-]+)[ =]$/.exec(before)?.[1];
  for (const rule of PLACEHOLDERS[name] ?? []) {
    if (rule.after !== undefined && rule.after !== flag) continue;
    if (rule.kind !== undefined && resourceKind(finding.resource_uid) !== rule.kind) continue;
    const value = valueOf(rule.source, finding);
    if (value !== null) return value;
  }
  return null;
}

function fillCommand(cli: string, finding: Finding): Command {
  const context = quoting(cli);
  const parts: CommandPart[] = [];
  const unresolved: string[] = [];
  const text = (t: string) => {
    const last = parts[parts.length - 1];
    if (last !== undefined && last.kind === "text") last.text += t;
    else if (t !== "") parts.push({ kind: "text", text: t });
  };
  let from = 0;
  for (const match of cli.matchAll(PLACEHOLDER)) {
    const start = match.index;
    text(cli.slice(from, start));
    from = start + match[0].length;
    const name = match[1];
    const value = fill(name, cli.slice(0, start), finding);
    const quoted =
      value === null
        ? null
        : SHELL_SAFE.test(value)
          ? value
          : context[start] === "none"
            ? `'${value.replace(/'/g, `'\\''`)}'`
            : null;
    if (quoted === null) {
      parts.push({ kind: "placeholder", name });
      if (!unresolved.includes(name)) unresolved.push(name);
    } else {
      parts.push({ kind: "value", text: quoted });
    }
  }
  text(cli.slice(from));
  const copied = parts.map((p) => (p.kind === "placeholder" ? `<${p.name}>` : p.text)).join("");
  return { label: COMMAND_LABEL, text: copied, parts, unresolved };
}
